import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TimelineEvent, ActivityTyp, ActivityTypConfig, SystemEventConfig, FieldChange } from '../../types/activity';
import { ActivityIcon } from './ActivityIcon';

interface TimelineItemProps {
  event: TimelineEvent;
  onEdit?: (activityId: string) => void;
  onDelete?: (activityId: string) => void;
}

export function TimelineItem({ event, onEdit, onDelete }: TimelineItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if this is an EMAIL activity with HTML content
  const isEmailActivity = event.type === 'activity' && event.eventType === 'EMAIL';
  const emailHtml = event.metadata?.emailHtml as string | undefined;

  // Notes referencing a Bedarf (e.g. per-Bedarf "nicht geeignet") carry the
  // Bedarf reference in their metadata so we can render a clickable link.
  const bedarfLinkId = event.metadata?.bedarfId as string | undefined;
  const bedarfLinkName = event.metadata?.bedarfName as string | undefined;

  const getEventConfig = () => {
    if (event.type === 'activity') {
      return ActivityTypConfig[event.eventType as ActivityTyp] || {
        label: event.eventType,
        color: 'text-gray-700',
        bgColor: 'bg-gray-100',
      };
    }
    return SystemEventConfig[event.eventType] || {
      label: event.eventType,
      color: 'text-gray-700',
      bgColor: 'bg-gray-100',
    };
  };

  const config = getEventConfig();
  const activityId = event.metadata?.activityId as string | undefined;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `Vor ${diffMins} Min.`;
    if (diffHours < 24) return `Vor ${diffHours} Std.`;
    if (diffDays < 7) return `Vor ${diffDays} Tagen`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserName = () => {
    if (!event.user) return 'System';
    return `${event.user.firstName} ${event.user.lastName}`;
  };

  return (
    <div className="relative flex gap-x-4">
      {/* Timeline line */}
      <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
        <div className="w-px bg-gray-200" />
      </div>

      {/* Icon */}
      <div
        className={`relative flex h-6 w-6 flex-none items-center justify-center rounded-full ${config.bgColor}`}
      >
        <span className={config.color}>
          <ActivityIcon type={event.type} eventType={event.eventType} size="sm" />
        </span>
      </div>

      {/* Content */}
      <div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200 bg-white">
        <div className="flex justify-between gap-x-4">
          <div className="py-0.5 text-xs leading-5 text-gray-500">
            <span className="font-medium text-gray-900">{getUserName()}</span>{' '}
            {event.type === 'activity' ? 'hat' : ''}{' '}
            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${config.bgColor} ${config.color}`}>
              {config.label}
            </span>{' '}
            {event.type === 'activity' ? 'erstellt' : ''}
          </div>
          <time className="flex-none py-0.5 text-xs leading-5 text-gray-500">
            {formatDate(event.occurredAt)}
          </time>
        </div>

        {/* Title */}
        {event.title && event.type === 'activity' && (
          <p className="text-sm font-medium text-gray-900 mt-1">{event.title}</p>
        )}

        {/* Structured field changes (UPDATE events) */}
        {event.type === 'system' && event.metadata?.changes && event.metadata.changes.length > 0 && (
          <dl className="mt-2 space-y-1">
            {event.metadata.changes.map((change: FieldChange) => (
              <div key={change.field} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <dt className="font-medium text-gray-700">{change.label}:</dt>
                <dd className="text-gray-700">
                  {change.oldValue === null && change.newValue === null ? (
                    <span className="italic text-gray-500">geändert</span>
                  ) : (
                    <>
                      <span className="text-gray-500 line-through">{change.oldValue}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="text-gray-900">{change.newValue}</span>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* Description (hidden when structured changes are present) */}
        {event.description !== null && event.description !== undefined && !(event.type === 'system' && event.metadata?.changes && event.metadata.changes.length > 0) && (
          <div className="mt-2">
            {isEmailActivity && emailHtml ? (
              // EMAIL with HTML: render formatted
              <>
                <div
                  className={`prose prose-sm max-w-none bg-gray-50 rounded-md p-3 ${
                    !isExpanded ? 'max-h-24 overflow-hidden' : ''
                  }`}
                  dangerouslySetInnerHTML={{ __html: emailHtml }}
                />
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  {isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                </button>
              </>
            ) : (
              // Non-EMAIL or no HTML: plain text with existing logic
              <>
                {/* whitespace-pre-line keeps the line breaks of multi-line notes such as
                    the Vorstellungsgespräch protocol, which otherwise collapse into one
                    run-on paragraph. */}
                <p
                  className={`text-sm text-gray-700 whitespace-pre-line ${
                    !isExpanded && event.description.length > 200 ? 'line-clamp-3' : ''
                  }`}
                >
                  {event.description}
                </p>
                {event.description.length > 200 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    {isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Clickable Bedarf reference for notes that carry one */}
        {event.type === 'activity' && bedarfLinkId && bedarfLinkName && (
          <div className="mt-2">
            <Link
              to={`/bedarfe/${bedarfLinkId}`}
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
            >
              Bedarf: {bedarfLinkName}
            </Link>
          </div>
        )}

        {/* Duration for calls/meetings */}
        {typeof event.metadata?.duration === 'number' && event.metadata.duration > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            Dauer: {event.metadata.duration} Min.
          </p>
        )}

        {/* Actions for activities */}
        {event.type === 'activity' && activityId && (onEdit || onDelete) && (
          <div className="mt-2 flex gap-x-2">
            {onEdit && (
              <button
                onClick={() => onEdit(activityId)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Bearbeiten
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(activityId)}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Löschen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
